/**
 * mapView (browser side): the working-area box every map opens on, as
 * getTenantConfig sends it (`working_area`: south, west, north, east),
 * and whether a point lies inside it.
 *
 * The server computes the box from the tenant's staff home points
 * (api/_shared/mapView.ts); the browser only reads it. A missing,
 * non-finite or inverted box reads as the whole country, the server's
 * COUNTRY_BOUNDS, held equal to this twin by a parity test
 * (tests/ui/mapView.test.ts).
 */

import type { TenantConfigResponse } from '@/lib/tenantConfig';

export interface MapBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface LatLngPoint {
  lat: number;
  lng: number;
}

/** The contiguous United States: the whole country, at a low zoom. */
export const COUNTRY_BOUNDS: MapBounds = { south: 24.5, west: -125, north: 49.5, east: -66.9 };

function finiteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** The config's working area when its four numbers are finite with south
 *  below north and west below east; the whole country otherwise. */
export function readWorkingArea(response: TenantConfigResponse | undefined): MapBounds {
  const raw = response?.working_area;
  if (typeof raw !== 'object' || raw === null) return COUNTRY_BOUNDS;
  const { south, west, north, east } = raw as Record<string, unknown>;
  if (!finiteNumber(south) || !finiteNumber(west) || !finiteNumber(north) || !finiteNumber(east)) return COUNTRY_BOUNDS;
  if (!(south < north) || !(west < east)) return COUNTRY_BOUNDS;
  return { south, west, north, east };
}

/** True when the point lies inside the box, its edges included. */
export function insideBounds(point: LatLngPoint, bounds: MapBounds): boolean {
  return (
    point.lat >= bounds.south && point.lat <= bounds.north && point.lng >= bounds.west && point.lng <= bounds.east
  );
}
